import { StandardEvents } from '@shopify/events';

if (!customElements.get('vics-art-size-guide')) {
  class VicsArtSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.unit = 'in';
      this.furniture = 'sofa';
      this.widthCm = 84 * 2.54;
      this.labels = JSON.parse(this.querySelector('[data-guide-labels]').textContent);
      this.size = this.querySelector('[data-guide-size]');
      this.frame = this.querySelector('[data-guide-frame]');
      this.widthInput = this.querySelector('[data-guide-width]');
      this.scene = this.querySelector('[data-guide-scene]');
      this.art = this.querySelector('[data-guide-art]');
      this.furn = this.querySelector('[data-guide-furniture]');
      this.image = this.querySelector('[data-guide-image]');
      this.dialog = this.closest('dialog');
      this.preloadObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.image.loading = 'eager';
        this.prepareImage();
        this.preloadObserver.disconnect();
      }, {rootMargin: '200px'});
      document.querySelectorAll('[data-vics-size-guide]').forEach(trigger => {
        if (trigger.dataset.vicsSizeGuide === this.dialog.id) this.preloadObserver.observe(trigger);
      });
      this.sizes = Array.from(this.size.options).map(option => ({value: option.value, ...this.parseSize(option.value)})).filter(item => item.w > 0 && item.h > 0);
      this.onOpen = event => {
        const trigger = event.target.closest('[data-vics-size-guide]');
        if (trigger?.dataset.vicsSizeGuide !== this.dialog.id) return;
        const picker = this.picker = trigger.closest('variant-picker');
        const select = picker?.querySelectorAll('select.variant-option__select')[Number(this.dataset.sizeIndex)];
        const matching = Array.from(picker?.querySelectorAll('select') || []).find(el => /size/i.test(el.name));
        if (matching || select) this.size.value = (matching || select).value;
        if (this.frame) {
          const availableFinishes = Array.from(this.frame.options).map(option => option.value);
          const checked = Array.from(picker?.querySelectorAll('input:checked') || []).find(el => availableFinishes.includes(el.value));
          const selected = Array.from(picker?.querySelectorAll('select') || []).find(el => el !== (matching || select) && availableFinishes.includes(el.value));
          if (checked || selected) this.frame.value = (checked || selected).value;
        }
        this.querySelector('[data-guide-error]').hidden = true;
        this.image.fetchPriority = 'high';
        this.image.loading = 'eager';
        this.prepareImage();
        requestAnimationFrame(() => this.render());
      };
      document.addEventListener('click', this.onOpen);
      this.addEventListener('click', event => {
        const furniture = event.target.closest('[data-guide-furniture-choice]');
        const unit = event.target.closest('[data-guide-unit]');
        const step = event.target.closest('[data-guide-step]');
        if (furniture) {
          this.furniture = furniture.dataset.guideFurnitureChoice;
          this.widthCm = this.furnitureSpec()[0];
          this.updateWidthField();
        }
        if (unit) { this.unit = unit.dataset.guideUnit; this.updateWidthField(); }
        if (step) {
          const next = Number(this.widthInput.value) + Number(step.dataset.guideStep);
          this.widthCm = Math.max(60.96, Math.min(609.6, next * (this.unit === 'in' ? 2.54 : 1)));
          this.updateWidthField();
        }
        if (event.target.closest('[data-guide-recommended]')) this.size.value = this.recommended()?.value || this.size.value;
        if (event.target.closest('[data-guide-apply]')) this.apply();
        if (furniture || unit || step || event.target.closest('[data-guide-recommended]')) this.render();
      });
      this.addEventListener('change', event => {
        event.stopPropagation();
        if (event.target === this.widthInput) {
          const width = Number(this.widthInput.value);
          this.widthCm = Number.isFinite(width) && width > 0 ? Math.max(60.96, Math.min(609.6, width * (this.unit === 'in' ? 2.54 : 1))) : this.furnitureSpec()[0];
          this.updateWidthField();
        }
        this.render();
      });
      this.observer = new ResizeObserver(() => { if (this.dialog.open) this.render(); });
      this.observer.observe(this.scene);
      this.updateWidthField();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onOpen);
      this.observer?.disconnect();
      this.preloadObserver?.disconnect();
      this.ready = false;
    }
    prepareImage() {
      if (!this.image || this.image.dataset.loaded === 'true') return;
      this.image.onload = () => {
        if (this.dataset.sourceKind === 'product') this.cropProductArtwork();
        this.image.dataset.loaded = 'true';
      };
      this.image.onerror = () => {
        this.image.dataset.loaded = 'error';
        this.image.alt = this.querySelector('.vics-art-guide__product').textContent;
      };
      if (!this.image.getAttribute('src') && this.dataset.image) this.image.src = this.dataset.image;
      if (this.image.complete && this.image.naturalWidth > 0) this.image.onload();
    }
    parseSize(value) {
      const text = value.replace(/[″“”]/g, '"').replace(/[×]/g, 'x');
      const axes = [...text.split('/')[0].matchAll(/(\d+(?:\.\d+)?)\s*["'\s]*([hw])/gi)];
      const cm = text.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/i);
      const dimensions = {};
      axes.slice(0, 2).forEach((match, index) => {
        const axis = match[2].toLowerCase();
        dimensions[axis] = cm ? Number(cm[index + 1]) : Number(match[1]) * 2.54;
        dimensions[axis + 'In'] = Number(match[1]);
      });
      if (!dimensions.w || !dimensions.h) {
        if (cm) { dimensions.h = Number(cm[1]); dimensions.w = Number(cm[2]); }
        else {
          const plain = text.match(/(\d+(?:\.\d+)?)\s*["'\s]*x\s*(\d+(?:\.\d+)?)/i);
          if (plain) { dimensions.h = Number(plain[1]) * 2.54; dimensions.w = Number(plain[2]) * 2.54; }
        }
      }
      const pieces = Number(this.dataset.pieces) || 1;
      dimensions.each = /each|per piece/i.test(text) && pieces > 1;
      dimensions.overallW = dimensions.each ? dimensions.w * pieces + (pieces - 1) * 5 : dimensions.w;
      return dimensions;
    }
    furnitureSpec() {
      return ({sofa: [213.36, 82], bed: [193.04, 82], wall: [243.84, 0], console: [152.4, 80], dining: [182.88, 76], fireplace: [152.4, 110], desk: [137.16, 76], bench: [121.92, 46]})[this.furniture];
    }
    measure(cm) { return `${this.unit === 'in' ? (cm / 2.54).toFixed(1).replace(/\.0$/, '') : Math.round(cm)}${this.unit === 'in' ? '"' : ' cm'}`; }
    artMeasure(size, axis, overall = false) {
      if (this.unit === 'in' && size[axis + 'In'] && !(overall && size.each)) return `${size[axis + 'In']}"`;
      return this.measure(overall ? size.overallW : size[axis]);
    }
    recommended() {
      const heightReference = this.furniture === 'wall' ? 240 : 180;
      const score = item => Math.abs(item.overallW / this.widthCm - .67) + Math.max(0, item.h - heightReference) / 90;
      return this.sizes.reduce((best, item) => !best || score(item) < score(best) ? item : best, null);
    }
    updateWidthField() {
      this.widthInput.value = Math.round(this.unit === 'in' ? this.widthCm / 2.54 : this.widthCm);
      this.widthInput.min = this.unit === 'in' ? '24' : '61';
      this.widthInput.max = this.unit === 'in' ? '240' : '610';
      this.querySelector('[data-guide-unit-label]').textContent = this.unit;
      this.querySelectorAll('[data-guide-unit]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.guideUnit === this.unit)));
    }
    cropProductArtwork() {
      try {
        const resolution = 128;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = resolution;
        const ctx = canvas.getContext('2d', {willReadFrequently: true});
        ctx.drawImage(this.image, 0, 0, resolution, resolution);
        const pixels = ctx.getImageData(0, 0, resolution, resolution).data;
        const point = (x, y) => Array.from(pixels.slice((y * resolution + x) * 4, (y * resolution + x) * 4 + 3));
        const corners = [point(1, 1), point(126, 1), point(1, 126), point(126, 126)];
        const background = [0, 1, 2].map(channel => corners.reduce((total, color) => total + color[channel], 0) / 4);
        if (corners.some(color => color.some((value, channel) => Math.abs(value - background[channel]) > 8))) return;
        if (Math.max(...background) - Math.min(...background) > 8 || Math.min(...background) < 220) return;
        const rows = Array(resolution).fill(0), columns = Array(resolution).fill(0);
        for (let y = 0; y < resolution; y++) for (let x = 0; x < resolution; x++) {
          const index = (y * resolution + x) * 4;
          if (background.some((value, channel) => Math.abs(pixels[index + channel] - value) > 12)) { rows[y]++; columns[x]++; }
        }
        const rowThreshold = Math.max(2, Math.max(...rows) * .6), columnThreshold = Math.max(2, Math.max(...columns) * .6);
        const vertical = rows.map((value, index) => value > rowThreshold ? index : -1).filter(value => value >= 0);
        const horizontal = columns.map((value, index) => value > columnThreshold ? index : -1).filter(value => value >= 0);
        if (!vertical.length || !horizontal.length) return;
        let left = horizontal[0], right = horizontal.at(-1) + 1, top = vertical[0], bottom = vertical.at(-1) + 1;
        if (Math.min(left, top, resolution - right, resolution - bottom) < 2) return;
        const inset = Math.ceil(Math.min(right - left, bottom - top) * .018);
        left += inset; top += inset; right -= inset; bottom -= inset;
        const width = right - left, height = bottom - top;
        if (width < 12 || height < 12) return;
        Object.assign(this.image.style, {width: `${resolution * 100 / width}%`, height: `${resolution * 100 / height}%`, left: `${-100 * left / width}%`, top: `${-100 * top / height}%`});
        this.image.dataset.artworkDetected = 'true';
      } catch (_) {}
    }
    furnitureSvg() {
      const shapes = {
        sofa: '<rect x="12" y="8" width="176" height="54" rx="12"/><rect x="23" y="17" width="72" height="40" rx="7"/><rect x="105" y="17" width="72" height="40" rx="7"/><rect x="9" y="48" width="182" height="23" rx="4"/><rect x="1" y="38" width="19" height="35" rx="5"/><rect x="180" y="38" width="19" height="35" rx="5"/><path d="M16 73v7m168-7v7" fill="none"/>',
        bed: '<rect x="5" y="0" width="190" height="68" rx="6"/><rect x="18" y="8" width="75" height="28" rx="5"/><rect x="107" y="8" width="75" height="28" rx="5"/><rect x="0" y="35" width="200" height="39" rx="4"/><path d="M10 74v6m180-6v6" fill="none"/>',
        console: '<rect x="0" y="0" width="200" height="9"/><rect x="8" y="9" width="184" height="34"/><path d="M100 10v33M12 43v37m176-37v37M28 43v37m144-37v37" fill="none"/>',
        dining: '<rect x="0" y="0" width="200" height="10" rx="3"/><path d="M16 10v70m168-70v70M26 10l12 70m136-70-12 70" fill="none"/>',
        fireplace: '<path d="M0 0h200v10H0zM9 10h182v70h-42V33H51v47H9z"/><path d="M61 80V43h78v37" fill="none"/>',
        desk: '<rect x="0" y="0" width="200" height="9"/><rect x="137" y="9" width="52" height="61"/><path d="M12 9v71m176-10v10M137 30h52m-52 20h52" fill="none"/>',
        bench: '<rect x="0" y="0" width="200" height="17" rx="3"/><path d="M14 17v63m172-63v63M14 61h172" fill="none"/>',
        wall: ''
      };
      return shapes[this.furniture];
    }
    render() {
      const size = this.sizes.find(item => item.value === this.size.value) || this.sizes[0];
      const empty = !size;
      this.querySelector('[data-guide-empty]').hidden = !empty;
      this.querySelector('[data-guide-apply]').disabled = empty || this.busy;
      this.art.hidden = empty;
      if (empty || !this.scene.clientWidth) return;
      const furnitureHeight = this.furnitureSpec()[1];
      const sceneW = this.scene.clientWidth, sceneH = this.scene.clientHeight;
      const scale = Math.min((sceneW - (sceneW < 450 ? 96 : 136)) / Math.max(size.overallW, this.widthCm), (sceneH - 105) / (size.h + furnitureHeight + 28));
      const furnitureH = Math.max(1, furnitureHeight * scale);
      Object.assign(this.furn.style, {width: `${this.widthCm * scale}px`, height: `${furnitureH}px`, bottom: '43px'});
      Object.assign(this.art.style, {width: `${size.overallW * scale}px`, height: `${size.h * scale}px`, bottom: `${43 + furnitureH + 28 * scale}px`});
      this.querySelector('[data-guide-furniture-svg]').innerHTML = this.furnitureSvg();
      this.querySelector('[data-guide-furniture-width]').textContent = `${this.measure(this.widthCm)} ${this.labels.width}`;
      this.querySelector('[data-guide-art-width]').textContent = `${this.artMeasure(size, 'w', true)} ${this.labels.width}`;
      this.querySelector('[data-guide-art-height]').textContent = `${this.artMeasure(size, 'h')} ${this.labels.height}`;
      const finish = this.frame?.value.toLowerCase() || '';
      const unframed = !finish || /rolled|frameless|unframed/.test(finish);
      const frameColor = /gold/.test(finish) ? '#ab8c51' : /white/.test(finish) ? '#eeeae3' : /silver/.test(finish) ? '#a4a7a9' : /wood|walnut|brown/.test(finish) ? '#59402d' : '#29241e';
      this.art.dataset.unframed = String(unframed);
      this.art.style.setProperty('--guide-frame', frameColor);
      this.art.style.setProperty('--guide-frame-width', `${Math.max(1, Math.min(5, scale * 1.8))}px`);
      const shape = Math.abs(size.w / size.h - 1) < .04 ? this.labels.square : size.w > size.h ? this.labels.horizontal : this.labels.vertical;
      const pieces = Number(this.dataset.pieces) || 1;
      this.querySelector('[data-guide-caption]').textContent = `${this.artMeasure(size, 'h')} ${this.labels.height} × ${this.artMeasure(size, 'w')} ${this.labels.width}${size.each ? ` · ${this.labels.each}` : ''}`;
      this.querySelector('[data-guide-orientation]').textContent = `${shape} · ${pieces > 1 ? `${this.labels.set} ${pieces}` : this.labels.single}`;
      const recommended = this.recommended();
      const button = this.querySelector('[data-guide-recommended]');
      button.textContent = recommended ? `${this.artMeasure(recommended, 'h')} ${this.labels.height} × ${this.artMeasure(recommended, 'w')} ${this.labels.width}` : '';
      this.querySelectorAll('[data-guide-furniture-choice]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.guideFurnitureChoice === this.furniture)));
    }
    async nativeAction(target, type, action) {
      let pending;
      const capture = event => { if (event.target === target) pending = event.promise; };
      target.addEventListener(type, capture);
      try { action(); }
      finally { target.removeEventListener(type, capture); }
      if (!pending) throw new Error(this.labels.cartError);
      return pending;
    }
    async apply() {
      if (this.busy) return;
      const picker = this.picker;
      const sizeSelect = Array.from(picker?.querySelectorAll('select') || []).find(el => /size/i.test(el.name));
      if (!sizeSelect || !Array.from(sizeSelect.options).some(option => option.value === this.size.value)) return;
      const error = this.querySelector('[data-guide-error]');
      const button = this.querySelector('[data-guide-apply]');
      const controls = Array.from(this.querySelectorAll('button,input,select'));
      this.busy = true;
      error.hidden = true;
      controls.forEach(control => { control.disabled = true; });
      button.textContent = this.labels.adding;
      button.setAttribute('aria-busy', 'true');
      try {
      sizeSelect.value = this.size.value;
      picker.updateSelectedOption?.(sizeSelect);
      let changed = sizeSelect;
      if (this.frame) {
        const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(el => el.value === this.frame.value);
        const frameSelect = Array.from(picker.querySelectorAll('select')).find(el => el !== sizeSelect && Array.from(el.options).some(option => option.value === this.frame.value));
        if (radio) { radio.checked = true; changed = radio; }
        else if (frameSelect) { frameSelect.value = this.frame.value; changed = frameSelect; }
      }
      const { detail } = await this.nativeAction(picker, StandardEvents.productSelect, () => changed.dispatchEvent(new Event('change', {bubbles: true})));
      if (!detail?.resource?.id || detail.resource.available === false) throw new Error(this.labels.unavailable);
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (picker.dataset.templateProductMatch === 'true') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(detail.resource.id));
          history.replaceState({}, '', url);
      }
      const section = picker.closest('.shopify-section');
      const productForm = Array.from(section?.querySelectorAll('product-form-component') || []).find(form => form.dataset.productId === picker.dataset.productId);
      const form = productForm?.querySelector('form');
      const submit = form?.querySelector('[ref="addToCartButton"]');
      if (!form || !submit || submit.disabled || form.querySelector('[name="id"]')?.value !== String(detail.resource.id)) throw new Error(this.labels.unavailable);
      const quantity = productForm.querySelector('quantity-selector-component');
      const validation = quantity?.canAddToCart?.();
      if (!form.reportValidity() || validation?.canAdd === false) throw new Error(this.labels.cartError);
      const result = await this.nativeAction(productForm, StandardEvents.cartLinesUpdate, () => form.requestSubmit(submit));
      if (result?.detail?.didError) throw new Error(productForm.querySelector('[ref="addToCartTextError"]')?.textContent.trim() || this.labels.cartError);
      this.dialog.close();
      } catch (cause) {
        error.textContent = cause.message || this.labels.cartError;
        error.hidden = false;
      } finally {
        this.busy = false;
        controls.forEach(control => { control.disabled = false; });
        button.textContent = this.labels.addToCart;
        button.removeAttribute('aria-busy');
        this.render();
      }
    }
  }
  customElements.define('vics-art-size-guide', VicsArtSizeGuide);
}

